/**
 * V2PlanCard — the agent's video plan, rendered as a scannable artifact:
 * header hero, concept/style, avatar & voice (resolved live; tap the avatar
 * photo for a big preview, the voice icon to audition, the text to swap),
 * details chips with the live credit cost, the per-scene VO + visual table,
 * and a single bottom Generate button (the only thing that renders + charges).
 * To change length the user just asks the agent in chat.
 *
 * Resources resolve through the LIVE /heygen-chat-resource endpoint; the
 * optional "say my line" TTS preview uses v2-voice-speech (graceful
 * pre-deploy fallback to the voice's stock preview).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Bot,
  Film,
  Languages,
  Captions,
  Clock,
  Sparkles,
  Play,
  Square,
  Loader2,
  RectangleHorizontal,
  ChevronDown,
  X,
  Mic,
  Palette,
  Pencil,
  Plus,
  Trash2,
  Check,
  SlidersHorizontal,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { apiService } from '@/api/apiService';
import { v2Service } from '@/api/v2Service';
import { useUserStore } from '@/store/userStore';
import { playPreview, preloadPreview, stopPreview, isPreviewPlaying, isPreviewLoading, onPreviewChange } from '@/v2/audio';
import { calculateHeygenCredits } from '@/utils/constants';
import type { V2CardPlan, CardPlanSelection } from '@/v2/planMap';
import type { V2VideoPlan } from '@/api/v2Service';
import { AssetPickerModal, type PickedAsset } from './AssetPickerModal';
import { AiAvatarModal, PhotoAvatarModal, DigitalTwinModal } from './AvatarCreateModals';
import { CloneVoiceModal, DesignVoiceModal } from './VoiceCreateModals';
import { BrandKitModal } from './BrandKitModal';
import { toast } from '@/v2/toast';
import { useUpgradeModal } from '@/v2/upgradeModal';
import { FREE_MAX_DURATION_SEC, formatLength } from '@/v2/freeCap';
import type { V2CreateAvatarResult, V2CloneVoiceResult, V2Voice } from '@/api/v2Types';

// Inline-creation actions offered from the plan card's pickers (same set as the
// home composer) — so creating an avatar/voice/brand finishes HERE, on the chat
// page, never redirecting to the assets page.
type PlanCreateAction = 'avatar-ai' | 'avatar-photo' | 'avatar-twin' | 'voice-clone' | 'voice-design' | 'brand-new';

// ---------------------------------------------------------------------------
// Resource resolution (shape per heygen-chat-resource:
//   { resource_id, resource_type, url, thumbnail_url, preview_url, metadata })
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Resource = Record<string, any>;
type ResourceCache = Map<string, Resource | null>;

const IMG_KEYS = ['thumbnail_url', 'preview_url', 'url', 'image_url', 'preview_image_url', 'poster_url', 'avatar_url'];

function pickImageUrl(res?: Resource | null): string | undefined {
  if (!res) return undefined;
  for (const k of IMG_KEYS) {
    const v = res[k];
    if (typeof v === 'string' && v.startsWith('http')) return v;
  }
  const meta = res.metadata;
  if (meta && typeof meta === 'object') {
    for (const k of IMG_KEYS) {
      const v = (meta as Resource)[k];
      if (typeof v === 'string' && v.startsWith('http')) return v;
    }
  }
  return undefined;
}

function pickAudioUrl(res?: Resource | null): string | undefined {
  if (!res) return undefined;
  for (const k of ['preview_audio_url', 'url']) {
    const v = res[k] ?? (res.metadata as Resource | undefined)?.[k];
    if (typeof v === 'string' && v.startsWith('http')) return v;
  }
  return undefined;
}

function pickName(res?: Resource | null): string | undefined {
  if (!res) return undefined;
  for (const k of ['display_name', 'name', 'title', 'label']) {
    const v = res[k] ?? (res.metadata as Resource | undefined)?.[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}

function useResource(userId: string | null, orderId: string, resourceId: string | null | undefined, cache: ResourceCache) {
  const [res, setRes] = useState<Resource | null>(() => (resourceId ? cache.get(resourceId) ?? null : null));
  useEffect(() => {
    if (!userId || !resourceId) return;
    if (cache.has(resourceId)) { setRes(cache.get(resourceId) ?? null); return; }
    let cancelled = false;
    apiService.getHeygenChatResource(userId, orderId, resourceId)
      .then((r) => { if (!cancelled) { cache.set(resourceId, r); setRes(r); } })
      // Re-render on failure too (was a silent cache.set) so callers can tell
      // "still fetching" from "resolved, no clip".
      .catch(() => { if (!cancelled) { cache.set(resourceId, null); setRes(null); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orderId, resourceId]);
  return res;
}

// [[resource_id]] or [[resource_id|Label]]
const REF_RE = /\[\[([A-Za-z0-9_-]+)(?:\|([^\]]+))?\]\]/g;

function splitRefs(text: string): Array<{ t: 'text'; value: string } | { t: 'ref'; id: string; label?: string }> {
  const out: Array<{ t: 'text'; value: string } | { t: 'ref'; id: string; label?: string }> = [];
  let last = 0;
  for (const m of text.matchAll(REF_RE)) {
    if (m.index! > last) out.push({ t: 'text', value: text.slice(last, m.index) });
    out.push({ t: 'ref', id: m[1], label: m[2] });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ t: 'text', value: text.slice(last) });
  return out;
}

function ResourceChip({
  userId, orderId, id, label, cache,
}: { userId: string | null; orderId: string; id: string; label?: string; cache: ResourceCache }) {
  const res = useResource(userId, orderId, id, cache);
  const img = pickImageUrl(res);
  const name = label || pickName(res) || id.slice(0, 14);
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-0.5 rounded-md bg-primary/10 border border-primary/25 text-primary text-[11px] font-medium align-middle">
      {img
        ? <img src={img} alt="" className="w-4 h-4 rounded-sm object-cover" />
        : <Film className="w-3 h-3" />}
      {name}
    </span>
  );
}

function RichText({ text, userId, orderId, cache }: { text: string; userId: string | null; orderId: string; cache: ResourceCache }) {
  const parts = useMemo(() => splitRefs(text), [text]);
  return (
    <>
      {parts.map((p, i) =>
        p.t === 'text'
          ? <span key={i}>{p.value}</span>
          : <ResourceChip key={i} userId={userId} orderId={orderId} id={p.id} label={p.label} cache={cache} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Avatar / voice selector rows (HeyGen cardboard style — click opens the
// full picker window when the plan is still editable)
// ---------------------------------------------------------------------------

function SelectorShell({ label, onClick, clickable, children }: {
  label: string; onClick?: () => void; clickable: boolean; children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-semibold text-text-primary mb-2">{label}</p>
      <div
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? onClick : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick?.(); } : undefined}
        className={cn(
          'flex items-center gap-3 p-2.5 rounded-2xl border border-glass-border bg-surface-light/40',
          clickable && 'cursor-pointer hover:border-primary/40 transition-colors'
        )}
      >
        {children}
        {clickable && <ChevronDown className="w-4 h-4 text-text-tertiary shrink-0 ml-auto" />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export interface V2PlanCardProps {
  orderId: string;
  plan: V2CardPlan;
  /** chat_active + not yet approved → actions are live */
  actionable: boolean;
  /** Start collapsed (older plans / already-generated plans) — still expandable. */
  collapsedDefault?: boolean;
  /** Bump to force this card OPEN (e.g. clicking "this plan" in a generate note). */
  expandSignal?: number;
  busy?: boolean;
  /** Pulse/glow the Generate button — the free funnel's "this is your next step"
   *  steer (replaces an arrow overlay, which is fragile across locales/screens). */
  glowGenerate?: boolean;
  onApprove: (durationSec: number, avatarId?: string, voiceId?: string, brandRef?: string) => void;
  /** The original planner plan — needed to reconstruct an edited plan on Save. */
  rawPlan?: V2VideoPlan;
  /** Persist a MANUAL plan edit (title/script/style/scenes). Enables the Edit button. */
  onSavePlan?: (plan: V2VideoPlan) => void;
  /** False hides the Edit button + editor — a provider plan is the provider's, not ours. */
  editable?: boolean;
  /** The provider's card was declined → say so instead of inviting changes to it. */
  declined?: boolean;
  /** The director replaced this plan after a revision asked for in chat → say which
   *  card is the live one instead of inviting changes to a plan nobody can approve. */
  superseded?: boolean;
  /** The price in VIVIDEO credits when the backend quoted one; otherwise the
   *  duration rule (calculateHeygenCredits) prices the card as it always has. */
  credits?: number;
  /** Persist an in-card avatar/voice/brand pick to the ORDER (project-level), so the
   *  choice sticks across plan cards + reload and Generate from ANY card uses the
   *  latest — never a stale value from an older card. */
  onSelectAsset?: (patch: Partial<CardPlanSelection>) => void;
  /** The free length cap applies: a free user is told about it. */
  lengthCap?: boolean;
  /** Pro: ask the planner for the full length the customer asked for (a chat message). */
  onPlanFullLength?: (message: string) => void;
}

export function V2PlanCard({ orderId, plan, actionable, collapsedDefault, expandSignal, busy, glowGenerate, onApprove, rawPlan, onSavePlan, onSelectAsset, editable = true, declined, superseded, credits: quotedCredits, lengthCap, onPlanFullLength }: V2PlanCardProps) {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  // OUR credit balance (Vivideo Users table — not HeyGen platform credits).
  const balance = useUserStore((s) => s.user?.remainingCredit ?? 0);
  const isPro = useUserStore((s) => s.isPro);
  const cacheRef = useRef<ResourceCache>(new Map());
  const cache = cacheRef.current;

  const scenes = plan.scenes || [];
  // The plan's own duration is the source of truth — it's what actually
  // renders, and the only thing we charge for. To change it the user just asks
  // the agent ("make it longer") in the chat; there's no manual slider.
  const planDuration = Math.max(5, Math.min(300, Number(plan.duration_seconds) || scenes[scenes.length - 1]?.end || 30));
  const requestedSec = Number(plan.requested_duration_sec) || 0;
  const requestedLength = formatLength(requestedSec);
  const [collapsed, setCollapsed] = useState(collapsedDefault ?? false);
  // Auto-(un)collapse when the plan's role changes — e.g. it gets superseded by
  // a newer plan, or a video is generated from it — while still letting the user
  // expand/collapse manually afterwards.
  useEffect(() => { setCollapsed(collapsedDefault ?? false); }, [collapsedDefault]);
  // Force-open when the chat asks ("Generating … from this plan" → click "this
  // plan"). Ignores the initial 0/undefined; every later bump expands this card.
  useEffect(() => { if (expandSignal) setCollapsed(false); }, [expandSignal]);
  const [picker, setPicker] = useState<'avatar' | 'voice' | 'brand' | null>(null);
  // Avatar/voice/brand rows collapsed behind "Advanced options" (mirrors Home's
  // composer). The collapsed strip still shows the agent's picks — changing them
  // is the advanced move, seeing them is not.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [createModal, setCreateModal] = useState<PlanCreateAction | null>(null);
  // Select what was just created into THIS card (so Generate uses it). Instant assets
  // (designed voice, brand) select immediately; avatars / cloned voices train in the
  // background — we select the ready ones and leave a breadcrumb for the rest.
  const onAvatarCreated = (r: V2CreateAvatarResult) => {
    const realPerson = r.kind === 'digital_twin' || r.kind === 'photo';
    if (r.look_id && !realPerson) {
      setAvatarOverride({ kind: 'avatar', id: r.look_id, label: r.display_name });
      onSelectAsset?.({ avatarId: r.look_id, avatarName: r.display_name });
    } else toast.info(t('v2PlanCard.avatarBeingPrepared', { name: r.display_name }));
  };
  const onVoiceCloned = (r: V2CloneVoiceResult) => {
    if (r.status === 'ready' && r.voice_clone_id) {
      setVoiceOverride({ kind: 'voice', id: r.voice_clone_id, label: r.display_name });
      onSelectAsset?.({ voiceId: r.voice_clone_id, voiceName: r.display_name });
    } else toast.info(t('v2PlanCard.voiceProcessing', { name: r.display_name }));
  };
  const [editing, setEditing] = useState(false);
  // Manual plan edit is offered only on the live (actionable) plan, and only when
  // the parent wired up save + handed us the raw planner plan to rebuild from.
  const canEdit = editable && actionable && !!onSavePlan && !!rawPlan;
  // A newer plan / a generated video retires the live one → leave edit mode.
  useEffect(() => { if (!actionable) setEditing(false); }, [actionable]);
  const [avatarPreview, setAvatarPreview] = useState(false);
  const [avatarOverride, setAvatarOverride] = useState<PickedAsset | null>(null);
  const [voiceOverride, setVoiceOverride] = useState<PickedAsset | null>(null);
  const [brandOverride, setBrandOverride] = useState<PickedAsset | null>(null);
  const [speakBusy, setSpeakBusy] = useState(false);

  // Whether a real voice is chosen (home selection or a card override). When NOT,
  // it's "Auto-selected" — no HeyGen was initialised, so there's nothing to
  // audition; we show an inert placeholder instead of a play button. (The avatar's
  // auto state is conveyed by simply having no image → the robot (Bot) fallback.)
  const hasVoiceSel = !!(voiceOverride?.id || plan.voice_id);

  // The chat-resource lookup needs a live HeyGen session, which doesn't exist at
  // plan time — so only fall back to it for a BARE id with no explicit display
  // info (the legacy in-session flow). New flow uses the carried name/image;
  // auto-selected fetches nothing → genuinely no preview.
  const needAvatarRes = !avatarOverride && !plan.avatar_image && !plan.avatar_name && !!plan.avatar_id;
  const needVoiceRes = !voiceOverride && !plan.voice_name && !!plan.voice_id;
  const avatarRes = useResource(userId, orderId, needAvatarRes ? plan.avatar_id : null, cache);
  const voiceRes = useResource(userId, orderId, needVoiceRes ? plan.voice_id : null, cache);
  // A quoted price (the provider's approval card) wins — including a quoted 0;
  // only an unquoted card falls back to the duration rule.
  const credits = typeof quotedCredits === 'number' ? Math.max(0, Math.round(quotedCredits)) : calculateHeygenCredits(planDuration);

  // Prefer the explicit chosen-cast fields (from the home selection, carried on the
  // plan) over the session-scoped resource lookup.
  const avatarImg = avatarOverride?.imageUrl || plan.avatar_image || pickImageUrl(avatarRes);
  const avatarName = avatarOverride?.label || plan.avatar_name || pickName(avatarRes) || t('v2PlanCard.autoSelected');
  const voiceName = voiceOverride?.label || plan.voice_name || pickName(voiceRes) || t('v2PlanCard.autoSelected');
  // Prefer the carried stock sample (the exact clip auditioned in the picker) so the
  // card plays INSTANTLY like the home chatbox — only fall back to TTS (sayLine) when
  // there's genuinely no sample (auto-selected voices).
  const voiceAudio = voiceOverride?.previewAudioUrl || plan.voice_preview_url || pickAudioUrl(voiceRes);
  // Brand (optional) — logo + name, shown like the cast rows. "None" = no kit.
  const brandLogo = brandOverride?.imageUrl || plan.brand_logo;
  const brandName = brandOverride?.label || plan.brand_name || t('v2PlanCard.none');
  const hasBrandSel = !!(brandOverride?.id || plan.brand_ref);

  // Warm the selected voice's sample the moment we know it, so the play button
  // is instant rather than fetching the clip on click.
  useEffect(() => { preloadPreview(voiceAudio); }, [voiceAudio]);

  // Reflect live play/stop state on the button; stop our clip on unmount.
  const [, forcePlay] = useState(0);
  useEffect(() => onPreviewChange(() => forcePlay((n) => n + 1)), []);
  useEffect(() => {
    const url = voiceAudio;
    return () => { if (isPreviewPlaying(url)) stopPreview(); };
  }, [voiceAudio]);
  const voicePlaying = isPreviewPlaying(voiceAudio);
  const voiceLoading = isPreviewLoading(voiceAudio);

  // Auto-selected voice clips are fetched from HeyGen async (resource → enriched
  // preview_audio_url). `voiceResolving` = that fetch hasn't finished yet (the
  // cache has no entry). Until recently a click during this window did nothing,
  // which felt broken — now we queue the play and fire it the instant the clip
  // lands (or fall back to TTS if there's no stock clip).
  const voiceResolving = needVoiceRes && !cache.has(plan.voice_id!);
  const [queued, setQueued] = useState(false);
  useEffect(() => {
    if (!queued) return;
    if (voiceAudio) { setQueued(false); playPreview(voiceAudio); return; }
    if (!voiceResolving) { setQueued(false); void sayLine(); } // resolved, no clip → TTS
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, voiceAudio, voiceResolving]);

  // The voice button:
  //   ready   → play / (while playing or loading) stop.
  //   resolving (clip still fetching) → spinner + queue an auto-play.
  //   no clip → generate the first line in this voice (TTS).
  const onVoiceClick = () => {
    // Auto-selected → no voice was initialised, so there's nothing to preview.
    if (!hasVoiceSel) return;
    if (voicePlaying || voiceLoading || queued) { stopPreview(); setQueued(false); return; }
    if (voiceAudio) { playPreview(voiceAudio); return; }
    if (voiceResolving) {
      setQueued(true);
      // Backstop: if the fetch is unusually slow, fall back to TTS rather than spin forever.
      window.setTimeout(() => setQueued((q) => { if (q) void sayLine(); return false; }), 8000);
      return;
    }
    void sayLine();
  };

  const sayLine = () => {
    if (!userId || speakBusy) return;
    // INSTANT: play the voice's stock preview right away — no waiting on a
    // server-side TTS render (that was the lag). Only if there's no stock clip
    // do we fall back to generating the first line in this voice.
    if (voiceAudio) { playPreview(voiceAudio); return; }
    const firstLine = plan.scenes?.find((s) => s.vo_script)?.vo_script?.slice(0, 140);
    // CRITICAL fallback: for an AUTO-SELECTED voice (no override) the chat-resource
    // often returns no audio URL AND no voice_id in metadata — so without
    // plan.voice_id here, voiceId was undefined and the click silently did nothing
    // (the "voice preview not working" bug). plan.voice_id is the id HeyGen chose.
    const voiceId = voiceOverride?.id || plan.voice_id || (voiceRes?.metadata as Resource | undefined)?.voice_id || voiceRes?.voice_id;
    if (!firstLine || !voiceId) return;
    setSpeakBusy(true);
    v2Service.speak({ userId, text: firstLine, voiceId })
      .then((r) => playPreview(r.audio_url))
      .catch(() => undefined)
      .finally(() => setSpeakBusy(false));
  };

  // Use the dropdown override if the user changed it here, else the chosen-cast id
  // carried on the plan (the home selection). Empty → auto (avatar/voice) or no kit (brand).
  const generate = () => onApprove(
    planDuration,
    avatarOverride?.id || plan.avatar_id || undefined,
    voiceOverride?.id || plan.voice_id || undefined,
    brandOverride?.id || plan.brand_ref || undefined,
  );

  return (
    <div className={cn(
      'rounded-2xl overflow-hidden border backdrop-blur-sm shadow-glow-sm animate-slide-up transition-colors duration-300',
      collapsed ? 'border-primary/40 bg-primary/10' : 'border-glass-border bg-surface/80'
    )}>
      {/* Top bar — click anywhere to toggle. Collapsed = a thicker purple banner. */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className={cn(
          'w-full flex items-center gap-3 text-left transition-all duration-300',
          collapsed ? 'px-5 py-4 hover:bg-primary/15' : 'px-4 py-3 border-b border-glass-border hover:bg-surface-light/30'
        )}
      >
        <span className={cn(
          'flex items-center justify-center w-7 h-7 rounded-lg shrink-0 transition-colors',
          collapsed ? 'bg-primary/20 text-primary' : 'text-text-secondary'
        )}>
          <ChevronDown className={cn('w-4 h-4 transition-transform duration-300', collapsed && '-rotate-90')} />
        </span>
        {collapsed ? (
          <>
            <Film className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-bold text-text-primary truncate">{plan.title || t('v2PlanCard.videoPlan')}</span>
            <div className="flex-1" />
            {/* Credit cost shown to PRO only — free users never see a price, so generation reads as free. */}
            {actionable && isPro() && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/20 border border-primary/40 text-xs font-bold text-primary shrink-0">
                <Sparkles className="w-3.5 h-3.5" /> {t('v2PlanCard.creditsCount', { count: credits })}
              </span>
            )}
          </>
        ) : (
          <span className="text-sm font-semibold text-text-secondary">{t('v2PlanCard.collapsePlan')}</span>
        )}
      </button>

      {/* Body — smooth height animation via grid-rows (0fr ⇄ 1fr) */}
      <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]')}>
        <div className="min-h-0 overflow-hidden">
          {/* Title banner (gradient only — no cover image) */}
          <div className="relative h-24 sm:h-28 bg-gradient-to-r from-primary/30 via-accent/25 to-primary/20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            {canEdit && !editing && (
              <button
                onClick={() => setEditing(true)}
                title={t('v2PlanCard.editTitle')}
                className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm border border-white/15 text-xs font-semibold text-white transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> {t('v2PlanCard.edit')}
              </button>
            )}
            <h3 className="absolute bottom-3 left-5 right-5 text-xl sm:text-2xl font-extrabold text-white leading-tight drop-shadow">
              {plan.title || t('v2PlanCard.untitledVideo')}
            </h3>
          </div>

          {editing && rawPlan ? (
            <PlanEditor
              plan={plan}
              rawPlan={rawPlan}
              busy={busy}
              onSave={(p) => { onSavePlan?.(p); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
          <div className="p-4 sm:p-5 space-y-5">
            {/* Script | Style */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {plan.concept_explanation && (
                // With no style text the script takes the whole row (LongVid sends none).
                <div className={plan.style_explanation ? undefined : 'sm:col-span-2'}>
                  <p className="text-sm font-semibold text-text-primary mb-1.5">{t('v2PlanCard.script')}</p>
                  <p className="text-sm text-text-secondary leading-relaxed">{plan.concept_explanation}</p>
                </div>
              )}
              {plan.style_explanation && (
                <div>
                  <p className="text-sm font-semibold text-text-primary mb-1.5">{t('v2PlanCard.style')}</p>
                  <p className="text-sm text-text-secondary leading-relaxed">{plan.style_explanation}</p>
                </div>
              )}
            </div>

            {/* LongVid only: the attached files and what the video uses each for. */}
            {Array.isArray(plan.files) && plan.files.some(f => f && f.name) && (
              <div data-testid="plan-files">
                <p className="text-sm font-semibold text-text-primary mb-1.5">{t('v2PlanCard.files')}</p>
                <ul className="space-y-1">
                  {plan.files.filter(f => f && f.name).map((f, i) => (
                    <li key={i} className="text-sm leading-relaxed">
                      <span className="font-medium text-text-primary break-all">{f.name}</span>
                      {f.use ? (
                        <span className="text-text-secondary">
                          {' — '}{f.use.trim().toLowerCase() === 'reference' ? t('v2PlanCard.fileReference') : f.use}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Avatar | Voice | Brand — same menus as Home's "Advanced options",
                tucked behind a toggle so the card leads with script + Generate.
                The collapsed strip still shows the agent's picks (transparency);
                expanding reveals the full selector rows to change them. */}
            <div>
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="w-full flex items-center gap-2 text-left group rounded-xl border border-glass-border bg-surface-light/60 px-3 py-2.5 hover:bg-surface-light transition-colors"
                aria-expanded={advancedOpen}
              >
                <SlidersHorizontal className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
                <span className="text-sm font-semibold text-text-primary shrink-0">{t('home.advancedOptions')}</span>
                <ChevronDown className={cn('w-4 h-4 text-text-tertiary shrink-0 ml-auto transition-transform group-hover:text-text-secondary', advancedOpen && 'rotate-180')} />
              </button>
              <div className={cn('grid transition-[grid-template-rows] duration-300 ease-out', advancedOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
                <div className="overflow-hidden min-h-0">
                  <div className={cn('grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 pt-4 transition-opacity duration-300', advancedOpen ? 'opacity-100' : 'opacity-0')}>
              <SelectorShell label={t('v2PlanCard.avatar')} clickable={actionable && editable} onClick={() => setPicker('avatar')}>
                {/* Photo → big preview; the rest of the row → selection menu. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (avatarImg) setAvatarPreview(true); }}
                  title={avatarImg ? t('v2PlanCard.seeLargerPreview') : undefined}
                  className={cn(
                    'w-12 h-12 rounded-xl overflow-hidden bg-[#121218]/60 flex items-center justify-center shrink-0 transition-all',
                    // Selected → click for a bigger preview. Auto-selected → nothing (no image
                    // to preview); de-emphasised so it doesn't look clickable.
                    avatarImg ? 'hover:ring-2 hover:ring-primary/40 cursor-pointer' : 'cursor-default',
                  )}
                >
                  {avatarImg
                    ? <img src={avatarImg} alt="" className="w-full h-full object-cover" />
                    : <Bot className="w-4 h-4 text-text-secondary" />}
                </button>
                <span className="text-sm font-semibold text-text-primary truncate">{avatarName}</span>
              </SelectorShell>
              <SelectorShell label={t('v2PlanCard.voice')} clickable={actionable && editable} onClick={() => setPicker('voice')}>
                {/* Click the MIC to hear the selected voice (auto-selected → nothing, mirrors
                    the avatar thumbnail). The rest of the row / chevron opens the picker. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); if (hasVoiceSel) onVoiceClick(); }}
                  title={hasVoiceSel ? (voicePlaying || voiceLoading || queued ? t('v2PlanCard.stop') : t('v2PlanCard.hearThisVoice')) : undefined}
                  className={cn(
                    'w-12 h-12 rounded-xl bg-[#121218]/60 flex items-center justify-center shrink-0 transition-all',
                    hasVoiceSel ? 'hover:ring-2 hover:ring-primary/40 cursor-pointer' : 'cursor-default',
                  )}
                >
                  {(speakBusy || voiceLoading || queued)
                    ? <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    : voicePlaying
                      ? <Square className="w-3.5 h-3.5 text-primary fill-primary" />
                      : <Mic className={cn('w-4 h-4', hasVoiceSel ? 'text-primary' : 'text-text-secondary')} />}
                </button>
                <span className="text-sm font-semibold text-text-primary truncate">{voiceName}</span>
              </SelectorShell>
              <SelectorShell label={t('v2PlanCard.brandKit')} clickable={actionable && editable} onClick={() => setPicker('brand')}>
                <span className="w-12 h-12 rounded-xl overflow-hidden bg-[#121218]/60 flex items-center justify-center shrink-0">
                  {brandLogo
                    ? <img src={brandLogo} alt="" className="w-full h-full object-contain p-1" />
                    : <Palette className={cn('w-4 h-4', hasBrandSel ? 'text-primary' : 'text-text-secondary')} />}
                </span>
                <span className="text-sm font-semibold text-text-primary truncate">{brandName}</span>
              </SelectorShell>
                  </div>
                </div>
              </div>
            </div>

            {/* Details chips */}
            <div>
              <p className="text-sm font-semibold text-text-primary mb-2">{t('v2PlanCard.details')}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-text-primary">
                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40">
                  <Clock className="w-3.5 h-3.5 text-text-secondary" /> {t('v2PlanCard.durationSeconds', { seconds: planDuration })}
                </span>
                {plan.orientation && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40 capitalize">
                    <RectangleHorizontal className={cn('w-3.5 h-3.5 text-text-secondary', plan.orientation === 'portrait' && 'rotate-90')} /> {plan.orientation}
                  </span>
                )}
                {plan.language && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40">
                    <Languages className="w-3.5 h-3.5 text-text-secondary" /> {plan.language}
                  </span>
                )}
                {plan.cc_enabled && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-glass-border bg-surface-light/40">
                    <Captions className="w-3.5 h-3.5 text-text-secondary" /> {t('v2PlanCard.enabled')}
                  </span>
                )}
                {isPro() && (
                  <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary font-semibold">
                    <Sparkles className="w-3.5 h-3.5" /> {t('v2PlanCard.creditsCount', { count: credits })}
                  </span>
                )}
              </div>
              {actionable && (
                <p className="text-[11px] text-text-tertiary mt-2">
                  {t('v2PlanCard.lengthHint')}
                </p>
              )}
            </div>

            {/* Scene breakdown — a Scene|Voiceover|Visual table on desktop; on mobile it
                becomes stacked, labelled blocks (the 3-column grid is unreadable on a phone). */}
            {scenes.length > 0 && (
              <div>
                <div className="hidden sm:grid grid-cols-[9rem_1fr_1fr] gap-x-4 px-3 pb-2 text-xs font-medium text-text-tertiary">
                  <span>{t('v2PlanCard.scene')}</span><span>{t('v2PlanCard.voiceover')}</span><span>{t('v2PlanCard.visual')}</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-glass-border divide-y divide-glass-border">
                  {scenes.map((s, i) => {
                    const aRoll = (s.type || '').toLowerCase() === 'a_roll';
                    return (
                      <div
                        key={i}
                        className={cn(
                          'p-3 flex flex-col gap-2.5 sm:grid sm:grid-cols-[9rem_1fr_1fr] sm:gap-x-4 sm:gap-y-0',
                          i % 2 === 0 ? 'bg-surface-light/40' : 'bg-transparent'
                        )}
                      >
                        {/* Scene meta — an inline header row on mobile, the first column on desktop */}
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:block min-w-0">
                          <p className="text-sm font-semibold text-text-primary leading-snug">{s.title || t('v2PlanCard.sceneNumber', { number: i + 1 })}</p>
                          <p className="text-[11px] text-text-tertiary tabular-nums sm:mt-0.5">{s.start}–{s.end}s</p>
                          <p className={cn('text-[11px] sm:mt-0.5', aRoll ? 'text-primary/90' : 'text-accent/90')}>
                            {aRoll ? t('v2PlanCard.aRoll') : t('v2PlanCard.bRoll')}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="sm:hidden text-[10px] font-bold uppercase tracking-wide text-text-tertiary mb-1">{t('v2PlanCard.voiceover')}</p>
                          {Array.isArray(s.speech) && s.speech.length > 0 ? (
                            <div className="space-y-1" data-testid="scene-speech">
                              {s.speech.map((line, k) => (
                                <p key={k} className="text-sm text-text-secondary leading-relaxed">
                                  <span className="font-semibold text-text-primary">
                                    {String(line.speaker || '').trim().toLowerCase() === 'narrator' ? t('v2PlanCard.narrator') : line.speaker}
                                  </span>{' '}
                                  {line.text}
                                </p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-text-secondary leading-relaxed">{s.vo_script}</p>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="sm:hidden text-[10px] font-bold uppercase tracking-wide text-text-tertiary mb-1">{t('v2PlanCard.visual')}</p>
                          <p className="text-sm text-text-secondary leading-relaxed">
                            {s.visual_explanation
                              ? <RichText text={s.visual_explanation} userId={userId} orderId={orderId} cache={cache} />
                              : null}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Free length cap: free users see the limit, Pro users can plan the full length. */}
            {lengthCap && !isPro() && (
              <p data-testid="free-length-note" className="text-[11px] text-text-tertiary">
                {t('v2PlanCard.freeLengthLimit', { seconds: FREE_MAX_DURATION_SEC })}{' '}
                <button
                  type="button"
                  onClick={() => useUpgradeModal.getState().show({ kind: 'pro_feature' })}
                  className="font-semibold text-primary hover:underline"
                >
                  {requestedSec > FREE_MAX_DURATION_SEC
                    ? t('v2PlanCard.upgradeForFullLength', { length: requestedLength })
                    : t('v2PlanCard.upgradeForLonger')}
                </button>
              </p>
            )}
            {lengthCap && isPro() && onPlanFullLength && requestedSec > planDuration && (
              <button
                type="button"
                data-testid="plan-full-length"
                disabled={busy}
                onClick={() => onPlanFullLength(t('v2PlanCard.fullLengthMessage', { length: requestedLength }))}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-xs font-semibold text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
              >
                <Clock className="w-3.5 h-3.5" /> {t('v2PlanCard.planFullLength', { length: requestedLength })}
              </button>
            )}

            {/* Bottom actions (long plans: Generate within reach) */}
            {actionable && (
              <div className="pt-1 space-y-2">
                {/* Only warn when the balance can't cover it (the reassuring
                    refund copy was removed per design). */}
                {isPro() && balance < credits && (
                  <p className="text-[11px] text-amber-300/90">
                    {t('v2PlanCard.insufficientBalancePrefix')} <span className="font-semibold">{t('v2PlanCard.creditsCount', { count: credits })}</span> {t('v2PlanCard.insufficientBalanceSuffix', { balance })}
                  </p>
                )}
                <button
                  onClick={generate}
                  disabled={busy}
                  className={cn(
                    'relative w-full py-4 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-base font-bold hover:opacity-90 hover:shadow-glow transition-all flex items-center justify-center gap-2.5 disabled:opacity-50',
                    glowGenerate && !busy && 'animate-pulse shadow-glow ring-2 ring-primary/50',
                  )}
                >
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5 fill-white" />}
                  <span>{t('v2PlanCard.generateVideo')}</span>
                  {/* Credits pinned to the right edge (label stays centered) — PRO only;
                      free users see no price so generation reads as free. */}
                  {isPro() && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1 rounded-lg bg-white/25 text-white text-sm font-extrabold shadow-sm">
                      <Sparkles className="w-4 h-4" /> {t('v2PlanCard.creditsCount', { count: credits })}
                    </span>
                  )}
                </button>
              </div>
            )}
            {!actionable && (superseded ? (
              /* Replaced by a newer plan: the verdict on this card, so the user knows
                 which one is live without hunting for the buttons. */
              <p className="text-xs font-bold flex items-center gap-1.5 text-text-tertiary">
                <Clock className="w-3.5 h-3.5" /> {t('agentProject.longvidCardSuperseded')}
              </p>
            ) : declined ? (
              <p className="text-xs font-bold flex items-center gap-1.5 text-text-tertiary">
                <X className="w-3.5 h-3.5" /> {t('v2StoryboardCard.declined')}
              </p>
            ) : (
              <p className="text-[11px] text-text-tertiary">
                {t('v2PlanCard.wantChangesHint')}
              </p>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Avatar / voice / brand picker windows (portal) */}
      {picker && (
        <AssetPickerModal
          kind={picker}
          selectedId={picker === 'avatar' ? avatarOverride?.id : picker === 'voice' ? voiceOverride?.id : brandOverride?.id}
          onClose={() => setPicker(null)}
          onSelect={(a) => {
            if (a.kind === 'avatar') { setAvatarOverride(a); onSelectAsset?.({ avatarId: a.id, avatarName: a.label, avatarImage: a.imageUrl ?? null }); }
            else if (a.kind === 'voice') { setVoiceOverride(a); onSelectAsset?.({ voiceId: a.id, voiceName: a.label, voicePreviewUrl: a.previewAudioUrl ?? null }); }
            else if (a.kind === 'brand') { setBrandOverride(a); onSelectAsset?.({ brandRef: a.id, brandName: a.label, brandLogo: a.imageUrl ?? null }); }
            setPicker(null);
          }}
          onCreate={(action) => { setPicker(null); setCreateModal(action as PlanCreateAction); }}
        />
      )}

      {/* Inline asset creation (finishes here on the chat page — no /assets redirect) */}
      {createModal === 'avatar-ai' && <AiAvatarModal onClose={() => setCreateModal(null)} onCreated={onAvatarCreated} />}
      {createModal === 'avatar-photo' && <PhotoAvatarModal onClose={() => setCreateModal(null)} onCreated={onAvatarCreated} />}
      {createModal === 'avatar-twin' && <DigitalTwinModal onClose={() => setCreateModal(null)} onCreated={onAvatarCreated} />}
      {createModal === 'voice-clone' && <CloneVoiceModal onClose={() => setCreateModal(null)} onCreated={onVoiceCloned} />}
      {createModal === 'voice-design' && <DesignVoiceModal onClose={() => setCreateModal(null)} onCreated={(v: V2Voice) => { setVoiceOverride({ kind: 'voice', id: v.voice_id, label: v.name }); onSelectAsset?.({ voiceId: v.voice_id, voiceName: v.name }); }} />}
      {createModal === 'brand-new' && (
        <BrandKitModal onClose={() => setCreateModal(null)} onSaved={(b) => { setBrandOverride({ kind: 'brand', id: b.brand_ref, label: b.name, imageUrl: b.logo_url }); onSelectAsset?.({ brandRef: b.brand_ref, brandName: b.name, brandLogo: b.logo_url }); }} />
      )}

      {/* Bigger avatar preview (tapping the avatar photo) */}
      {avatarPreview && avatarImg && createPortal(
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fade-in"
          onClick={() => setAvatarPreview(false)}
        >
          <div
            className="relative w-[min(86vw,340px)] rounded-3xl bg-surface border border-glass-border shadow-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[3/4] bg-[#121218]">
              <img src={avatarImg} alt={avatarName} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/10" />
              <div className="absolute bottom-0 inset-x-0 px-4 pb-3.5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/55">{t('v2PlanCard.avatar')}</p>
                <p className="text-base font-bold text-white truncate drop-shadow">{avatarName}</p>
              </div>
              <button
                onClick={() => setAvatarPreview(false)}
                className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 flex items-center justify-center text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanEditor — manual plan editing (title / script / style / scenes: add, remove,
// rewrite voiceover & visual, set track + timing). Emits an edited VideoPlan that
// the parent persists as the project's current plan — what Generate renders and
// what the agent revises next. The user's edits are authoritative (never re-"fixed").
// ---------------------------------------------------------------------------

type SceneDraft = { name: string; start: number; end: number; track: 'A Roll' | 'B Roll'; voiceover: string; visual: string; speech?: { speaker: string; text: string }[] };

function PlanEditor({ plan, rawPlan, busy, onSave, onCancel }: {
  plan: V2CardPlan;
  rawPlan: V2VideoPlan;
  busy?: boolean;
  onSave: (p: V2VideoPlan) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('v2');
  const [title, setTitle] = useState(plan.title || '');
  const [script, setScript] = useState(plan.concept_explanation || '');
  const [style, setStyle] = useState(plan.style_explanation || '');
  const [scenes, setScenes] = useState<SceneDraft[]>(() =>
    (plan.scenes || []).map((s) => ({
      name: s.title || '',
      start: Number(s.start) || 0,
      end: Number(s.end) || 0,
      track: s.type === 'a_roll' ? 'A Roll' : 'B Roll',
      voiceover: s.vo_script || '',
      visual: s.visual_explanation || '',
      ...(Array.isArray(s.speech) && s.speech.length ? { speech: s.speech } : {}),
    })),
  );

  const patchScene = (i: number, patch: Partial<SceneDraft>) =>
    setScenes((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const removeScene = (i: number) => setScenes((prev) => prev.filter((_, j) => j !== i));
  const addScene = () =>
    setScenes((prev) => {
      const start = prev.length ? prev[prev.length - 1].end : 0;
      return [...prev, { name: t('v2PlanCard.sceneNumber', { number: prev.length + 1 }), start, end: start + 5, track: 'B Roll', voiceover: '', visual: '' }];
    });

  // Total duration follows the scenes (max end). Credits track it live.
  const duration = scenes.reduce((m, s) => Math.max(m, Number(s.end) || 0), 0);
  const credits = calculateHeygenCredits(Math.max(5, duration || Number(plan.duration_seconds) || 30));

  const save = () => {
    const dur = Math.max(5, Math.min(300, duration || Number(plan.duration_seconds) || 30));
    const s = rawPlan.settings || ({} as V2VideoPlan['settings']);
    onSave({
      ...rawPlan,
      title: title.trim() || t('v2PlanCard.untitledVideo'),
      script: script.trim(),
      style: style.trim(),
      settings: {
        ...s,
        duration_sec: dur,
        orientation: s.orientation || plan.orientation || 'landscape',
        language: s.language || plan.language || 'English',
        captions_enabled: s.captions_enabled ?? plan.cc_enabled ?? true,
        credits_estimate: s.credits_estimate || '',
      },
      scenes: scenes.map((sc, i) => ({
        scene_id: i + 1,
        name: sc.name.trim() || t('v2PlanCard.sceneNumber', { number: i + 1 }),
        start_sec: Number(sc.start) || 0,
        end_sec: Number(sc.end) || 0,
        track: sc.track,
        voiceover: sc.voiceover,
        visual: sc.visual,
        ...(sc.speech ? { speech: sc.speech } : {}),
      })),
    });
  };

  const inputCls = 'w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50';
  const fieldCls = `${inputCls} resize-y`;
  const labelCls = 'block text-xs font-semibold text-text-secondary mb-1.5';

  return (
    <div className="p-5 space-y-5 animate-fade-in">
      <div className="flex items-start gap-2">
        <Pencil className="w-4 h-4 text-primary mt-0.5 shrink-0" />
        <p className="text-sm font-bold text-text-primary">
          {t('v2PlanCard.editingPlan')}
          <span className="block text-[11px] font-normal text-text-tertiary mt-0.5">{t('v2PlanCard.editingPlanHint')}</span>
        </p>
      </div>

      <div>
        <label className={labelCls}>{t('v2PlanCard.title')}</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder={t('v2PlanCard.titlePlaceholder')} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>{t('v2PlanCard.scriptSynopsis')}</label>
          <textarea value={script} onChange={(e) => setScript(e.target.value)} rows={3} className={fieldCls} placeholder={t('v2PlanCard.scriptPlaceholder')} />
        </div>
        <div>
          <label className={labelCls}>{t('v2PlanCard.style')}</label>
          <textarea value={style} onChange={(e) => setStyle(e.target.value)} rows={3} className={fieldCls} placeholder={t('v2PlanCard.stylePlaceholder')} />
        </div>
      </div>

      {/* Scenes */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-text-primary">{t('v2PlanCard.scenes')}</span>
          <span className="text-[11px] text-text-tertiary tabular-nums">{t('v2PlanCard.durationCreditsSummary', { seconds: duration, credits })}</span>
        </div>
        {scenes.map((sc, i) => (
          <div key={i} className="rounded-xl border border-glass-border bg-surface-light/30 p-3 space-y-2.5">
            {/* flex-wrap so the A/B-roll toggle + trash drop to a second line on
                narrow screens instead of overflowing the card; name input keeps a
                min width and takes the full row on mobile. */}
            <div className="flex flex-wrap items-center gap-2">
              <input value={sc.name} onChange={(e) => patchScene(i, { name: e.target.value })} className="flex-1 min-w-[7rem] bg-surface border border-glass-border rounded-lg px-2.5 py-1.5 text-sm font-semibold text-text-primary outline-none focus:border-primary/50" placeholder={t('v2PlanCard.sceneNumber', { number: i + 1 })} />
              <div className="flex rounded-lg border border-glass-border overflow-hidden shrink-0">
                {(['A Roll', 'B Roll'] as const).map((tr) => (
                  <button key={tr} onClick={() => patchScene(i, { track: tr })}
                    className={cn('px-2.5 py-1.5 text-[11px] font-semibold transition-colors', sc.track === tr ? 'bg-primary/20 text-primary' : 'text-text-tertiary hover:text-text-secondary')}>
                    {tr === 'A Roll' ? t('v2PlanCard.aRoll') : t('v2PlanCard.bRoll')}
                  </button>
                ))}
              </div>
              <button onClick={() => removeScene(i)} title={t('v2PlanCard.removeScene')} className="w-8 h-8 rounded-lg flex items-center justify-center text-text-tertiary hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 text-xs text-text-tertiary">
              <span>{t('v2PlanCard.from')}</span>
              <input type="number" min={0} value={sc.start} onChange={(e) => patchScene(i, { start: Number(e.target.value) })} className="w-16 bg-surface border border-glass-border rounded-md px-2 py-1 text-sm text-text-primary tabular-nums outline-none focus:border-primary/50" />
              <span>{t('v2PlanCard.to')}</span>
              <input type="number" min={0} value={sc.end} onChange={(e) => patchScene(i, { end: Number(e.target.value) })} className="w-16 bg-surface border border-glass-border rounded-md px-2 py-1 text-sm text-text-primary tabular-nums outline-none focus:border-primary/50" />
              <span>{t('v2PlanCard.sec')}</span>
            </div>
            <div>
              <label className={labelCls}>{t('v2PlanCard.voiceover')}</label>
              <textarea value={sc.voiceover} onChange={(e) => patchScene(i, { voiceover: e.target.value, speech: undefined })} rows={2} className={fieldCls} placeholder={t('v2PlanCard.voiceoverPlaceholder')} />
            </div>
            <div>
              <label className={labelCls}>{t('v2PlanCard.visual')}</label>
              <textarea value={sc.visual} onChange={(e) => patchScene(i, { visual: e.target.value })} rows={2} className={fieldCls} placeholder={t('v2PlanCard.visualPlaceholder')} />
            </div>
          </div>
        ))}
        <button onClick={addScene} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-glass-border text-sm font-semibold text-text-secondary hover:text-primary hover:border-primary/40 transition-colors">
          <Plus className="w-4 h-4" /> {t('v2PlanCard.addScene')}
        </button>
      </div>

      {/* Save / Cancel */}
      <div className="flex items-center gap-2 pt-1">
        <button onClick={save} disabled={busy || !scenes.length}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-95 transition-all disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t('v2PlanCard.saveChanges')}
        </button>
        <button onClick={onCancel} disabled={busy}
          className="px-4 py-3 rounded-xl border border-glass-border text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50">
          {t('v2PlanCard.cancel')}
        </button>
      </div>
    </div>
  );
}
